<template>
  <teleport to="head">
    <title>{{ HEAD_TITLE }} | Enroll</title>
  </teleport>
  <UnauthNav />
  <div class="lcu-enroll-page">
    <SecondaryNav />
    <div
      class="lcu-enroll-box"
      :class="{
        'lcu-box-large': enrollStep === 'fee'
      }">
      <div>
        <section class="section">
          <header class="lcu-section-header lcu-center-text">
            Inscribirse en Legacy Christian University
          </header>
        </section>
        <section class="section">
            <div class="coming-soon">
              <div class="row lcu-center-text">Próximamente: Inscripciones en línea</div>
            </div>
        </section>
      </div>
    </div>
  </div>
  <Footer />
</template>

<script>
import axios from 'axios';
import _ from 'lodash';
import vueRecaptcha from 'vue3-recaptcha2';
import { Country } from 'country-state-city';
import FormField from 'ç/forms/inputs/Field.vue';
import SpinLoader from 'ç/loaders/spin/Spin.vue';
import ButtonCta from 'ç/buttons/Cta.vue';
import FormValidator from '@/mixins/Validator.vue';
import Auth from '@/mixins/Auth.vue';
import passwordCriteria from '@/mixins/PasswordCriteria.vue';
import Tooltip from 'ç/tooltip/Tooltip.vue';
import BulletLegend from 'ç/bullet-legend/BulletLegend.vue';
import Footer from 'ç/footer/Footer.vue';
import UnauthNav from 'ç/nav/UnauthNav.vue';
import SecondaryNav from 'ç/nav/SecondaryNav.vue';
import { NOTIFICATION_SUCCESS, NOTIFICATION_ERROR } from '@/globals/constants';

const RE_CAPTCHA_ERROR = 'Hubo un error con el servicio de reCaptcha.';
const TERMS_LABEL = 'Acepto los términos y condiciones de LCU. ';

export default {
  name: 'enroll-page',
  mixins: [Auth, FormValidator, passwordCriteria],
  components: {
    UnauthNav,
    SpinLoader,
    Footer,
    ButtonCta,
    Tooltip,
    vueRecaptcha,
    BulletLegend,
    SecondaryNav,
    'form-field': FormField,
  },
  computed: {
    countries() {
      return this.allCountries.map(c => c.name);
    },
    getCurrentStep() {
      if (this.enrollStep === 'fee') {
        return 4;
      }
      if (this.enrollStep === 'confirm-account') {
        return 3;
      }
      if (this.enrollStep === 'security') {
        return 2;
      }
      return 1;
    },
    newPassError() {
      return this.validations.newPassword.errors.length > 0 || this.confirmError;
    },
    confirmPassError() {
      return this.validations.confirmPass.errors.length > 0 || this.confirmError;
    },
    showCriteriaError() {
      const input = this.validations.newPassword.value.replace(/\s+/g, '');
      return !this.criteriaPass && input !== '';
    },
  },
  data() {
    return {
      loaders: {
        security: false,
        confirmCode: false,
        confirmAndPay: false,
      },
      confirmPass: '',
      showCompletedEnrollment: false,
      enrollStep: 'profile',
      pais: 'pais',
      género: 'género',
      countryError: false,
      genderError: false,
      validations: {
        firstName: {
          value: '',
          required: true,
          errors: [],
        },
        lastName: {
          value: '',
          required: true,
          errors: [],
        },
        email: {
          value: '',
          required: true,
          errors: [],
        },
        birthday: {
          value: '',
          required: true,
          format: /^(0[1-9]|1[0-2]) \/ (0[1-9]|1\d|2\d|3[01]) \/ (19|20)\d{2}$/,
          errors: [],
        },
        phone: {
          value: '',
          required: true,
          errors: [],
        },
        newPassword: {
          value: '',
          required: true,
          errors: [],
        },
        confirmPass: {
          value: '',
          required: true,
          errors: [],
        },
        code: {
          value: '',
          required: true,
          errors: [],
        },
        cardNum: {
          value: '',
          required: true,
          errors: [],
        },
        expDate: {
          value: '',
          required: true,
          errors: [],
        },
        cvv: {
          value: '',
          required: true,
          errors: [],
        },
        name: {
          value: '',
          required: true,
          errors: [],
        },
      },
      agreeTerms: false,
      savePaymentMethod: true,
      confirmError: false,
      showNewPass: false,
      showConfirmPass: false,
      signupError: false,
      resendLoading: false,
      resendSuccess: false,
      reCaptchaKey: null,
      allCountries: [],
      discountApplied: false,
      discount: '',
      discountError: false,
      discountLoader: false,
      savings: false,
      initialPricing: {
        subtotal: '—',
        total: '—',
        taxes: '—',
        currency: 'usd',
      },
      pricing: {
        subtotal: '—',
        total: '—',
        taxes: '—',
        currency: 'usd',
      },
      TERMS_LABEL,
    };
  },
  created() {
    this.allCountries = Country.getAllCountries();
    this.resetHint();
    this.reCaptchaKey = process.env.VUE_APP_RECAPTCHA_KEY;
  },
  methods: {
    resetCaptcha() {
      if (this.$refs.vueRecaptcha) {
        this.$refs.vueRecaptcha.reset();
      }
    },
    validateSelects({ error, value }) {
      this[error] = false;
      if (this[value] === value || this[value] === '') {
        this[error] = true;
      }
      return {
        passed: !this[error],
      };
    },
    validateProfileStep() {
      try {
        const validation = this.validate([
          'firstName',
          'lastName',
          'email',
          'birthday',
          'phone',
        ]);
        const countryFailed = !this.validateSelects({ error: 'countryError', value: 'pais' }).passed;
        const genderFailed = !this.validateSelects({ error: 'genderError', value: 'género' }).passed;
        if (!validation.passed || countryFailed || genderFailed) {
          return;
        }
        this.enrollStep = 'security';
      } catch (error) {
        const message = error.message || error;
        setTimeout(() => {
          window.scrollTo(0, document.body.scrollHeight);
        }, 500);
        this.$log.error(message);
      }
    },
    async validateSecurityStep() {
      this.signupError = false;
      try {
        const validation = this.validate([
          'newPassword',
          'confirmPass',
        ]);
        if (!validation.passed) {
          return;
        }
        if (!this.criteriaPass) {
          return;
        }
        if (this.confirmError) {
          return;
        }
        await this.$refs.vueRecaptcha.execute();
      } catch (error) {
        const message = error.message || error;
        this.signupError = message;
        setTimeout(() => {
          window.scrollTo(0, document.body.scrollHeight);
        }, 500);
        this.$log.error(message);
      }
    },
    async onSignup(token) {
      this.resetCaptcha();
      this.signupError = false;
      try {
        this.loaders.security = true;
        const valid = await this.$validSession();
        if (valid) {
          this.$log.info('Session found, logging out.');
          await this.$logout();
          this.$clearAuthStorage();
          this.$store.dispatch('logoutUser');
        }
        const {
          firstName,
          lastName,
          newPassword,
          email,
        } = this.validations;
        const country = this.pais === 'pais' ? 'United States' : this.pais;
        await axios({
          method: 'post',
          url: process.env.VUE_APP_SIGNUP_ENDPOINT,
          data: {
            firstName: firstName.value,
            lastName: lastName.value,
            email: email.value,
            country,
            password: newPassword.value,
            token,
          },
        });
        await this.$cognitoLogin({
          user: email.value,
          pass: newPassword.value,
        });
      } catch (error) {
        if (error && error.code === 'UserNotConfirmedException') {
          this.enrollStep = 'confirm-account';
        } else {
          const message = error?.response?.data?.result || error.message || error;
          this.signupError = message;
          setTimeout(() => {
            window.scrollTo(0, document.body.scrollHeight);
          }, 500);
          this.$log.error(message);
        }
      } finally {
        this.loaders.security = false;
      }
    },
    async validateConfirmCode() {
      this.signupError = false;
      try {
        const validation = this.validate(['code']);
        if (!validation.passed) {
          return;
        }
        this.loaders.confirmCode = true;
        const {
          code,
          newPassword,
          email,
          birthday,
          phone,
        } = this.validations;
        await this.$confirmNewUser({
          code: code.value,
          user: email.value,
          pass: newPassword.value,
        });
        const payload = {
          birthday: birthday.value,
          gender: this.género,
          phone: phone.value,
        };
        await this.$validateSession();
        // Note: this will create the new user data in the db
        await this.$students({ method: 'getUserData', payload });
        const { price } = await this.$students({ method: 'getEnrollmentPricing' });
        this.$log.info('Enrollment price: ', price);
        this.pricing = price;
        this.initialPricing = _.cloneDeep(price);
        this.enrollStep = 'fee';
      } catch (e) {
        const message = e.message || e;
        this.signupError = message;
        this.$log.error(message);
      } finally {
        this.loaders.confirmCode = false;
      }
    },
    clearDiscount() {
      this.discountApplied = false;
      this.discount = '';
      this.pricing = _.cloneDeep(this.initialPricing);
      this.savings = false;
    },
    async onApplyDiscount() {
      if (this.discountLoader) {
        return;
      }
      this.discountError = false;
      if (this.discount === '') {
        this.discountError = this.formValidationMessages.required;
        return;
      }
      this.discountLoader = true;
      this.discountApplied = false;
      try {
        await this.$validateSession();
        const { USD } = await this.$students({
          method: 'couponCheck',
          payload: {
            productMapId: 'enrollmentFee',
            code: this.discount,
          },
        });
        this.pricing.total = USD.newTotal;
        this.pricing.taxes = USD.newTaxes;
        this.savings = USD.savings;
        this.discountApplied = true;
        this.$notify({
          title: NOTIFICATION_SUCCESS,
          text: 'Código de descuento válido',
          type: 'success',
        });
      } catch (e) {
        const message = e.message || e;
        this.discountError = message;
        this.pricing = _.cloneDeep(this.initialPricing);
        this.$log.error(message);
      } finally {
        this.discountLoader = false;
      }
    },
    validateDiscount() {
      if (this.discount === '') {
        this.discountError = this.formValidationMessages.required;
      } else {
        this.discountError = false;
      }
    },
    async confirmAndPay() {
      this.signupError = false;
      try {
        const validation = this.validate([
          'cardNum',
          'expDate',
          'cvv',
          'name',
        ]);
        if (!validation.passed) {
          return;
        }
        this.loaders.confirmAndPay = true;
        const {
          cardNum,
          expDate,
          cvv,
          name,
        } = this.validations;
        await this.$validateSession();
        const { updates } = await this.$students({
          method: 'payAndEnroll',
          payload: {
            discountCode: this.discount,
            card: {
              number: cardNum.value,
              cvc: cvv.value,
              name: name.value,
              expDate: expDate.value,
              savePaymentMethod: this.savePaymentMethod,
            },
          },
        });
        this.$store.dispatch('setGlobalValue', {
          item: this.user,
          key: 'fees',
          value: updates.fees,
        });
        this.showCompletedEnrollment = true;
      } catch (e) {
        const message = e.message || e;
        this.signupError = message;
        this.$log.error(message);
      } finally {
        this.loaders.confirmAndPay = false;
      }
    },
    recaptchaError() {
      this.signupError = RE_CAPTCHA_ERROR;
    },
    recaptchaFailed(reason) {
      this.signupError = RE_CAPTCHA_ERROR;
      this.$log.error(`reCaptcha failed: ${reason}`);
    },
    async resendCode() {
      try {
        this.resendLoading = true;
        const { email } = this.validations;
        await this.$resendNewUserCode({
          email: email.value,
        });
        this.resendSuccess = true;
        setTimeout(() => {
          this.resendSuccess = false;
        }, 2000);
      } catch (e) {
        const message = e.message || e;
        this.$notify({
          title: NOTIFICATION_ERROR,
          text: message,
          type: 'error',
          duration: -1,
        });
        this.$log.error(message);
      } finally {
        this.resendLoading = false;
      }
    },
    checkConfirm() {
      this.validations.newPassword.value = this.validations.newPassword.value.replace(/\s+/g, '');
      if (this.validations.confirmPass.value.trim() !== '') {
        this.checkPass();
      }
    },
    checkPass() {
      const { confirmPass, newPassword } = this.validations;
      if (newPassword.value === confirmPass.value) {
        this.confirmError = false;
      } else {
        this.confirmError = this.formValidationMessages.passwordMatch;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.lcu-enroll-page {
  background-color: $white;
  align-items: center;
  display: flex;
  flex-direction: column;
  padding: 0 0 $public-nav-height;

  .lcu-secondary-nav {
    margin: $public-nav-height 0;
  }

  @media only screen and (max-width: $mobile-max) {

    .lcu-secondary-nav {
      margin: 0 0 50px;
    }
  }
}

.lcu-enroll-logo {
  width: 100px;
}

.lcu-enroll-box {
  background-color: $white;
  padding: 0;
  width: 90%;

  @media screen and (min-width: $desktop-min) {
    width: 800px;
  }
}

.lcu-box-large {
    @media screen and (min-width: $desktop-min) {
    width: 1000px;
  }
}

.lcu-enroll-header {
  color: $legacy-primary;
  font-size: 24px;
}

.lcu-enroll-inputText {
  background-color: transparent;
  border: 0;
  border-bottom: 2px solid $legacy-primary;
  color: $legacy-primary;
  font-size: 21px;
  padding: 5px;
  width: 100%;

  &:focus-visible {
    @include no-focus;
    border-bottom: 2px solid $legacy-primary;
  }

  &:focus {
    @include no-focus;
    border-bottom: 2px solid $legacy-primary;
  }

  &:focus-within {
    @include no-focus;
    border-bottom: 2px solid $legacy-primary;
  }
}

.lcu-form-riser {
  color: $legacy-primary;
  display: inline-block;
  font-size: 21px;
  margin-left: 5px;
  pointer-events: none;
  position: absolute;
  transform: translateY(6px);
  transition-duration: .1s;
}

.lcu-form-risen {
  font-size: 13px;
  transform: translateY(-15px);
}

.lcu-enroll-selectWrap {
  font-size: 13px;
  position: relative;
}

.lcu-active-selectRequired {
    &:after {
      color: $danger;
      content: '\e819';
      display: inline-block;
      font-family: 'lcu';
      font-size: 9px;
      position: absolute;
      top: 18px;
      left: 90px;
    }
}

.lcu-enroll-select {
  appearance: none;
  background-color: $white;
  border: 2px solid $legacy-primary;
  color: $legacy-primary;
  font-size: 21px;
  padding: 8px 10px;
  width: 100%;

  &:focus {
    border-color: $legacy-primary;
    outline: none;
  }

  &:hover {
    cursor: pointer;
  }

  &:focus-visible {
    border-color: $legacy-primary;
    outline: none;
  }
}

.lcu-enroll-selectIcon {
  bottom: 10px;
  color: $legacy-primary;
  font-size: 14px;
  right: 10px;
  pointer-events: none;
  position: absolute;

  &:before {
    display: inline-block;
    content: '\e817';
    font-family: 'lcu';
  }
}

.lcu-passHint-group {
  background-color: $gray-lightest;
  color: $gray-dark;
  border: 1px solid $page-border;
}

.lcu-icon-eyeSlash {
  background-color: transparent;
  border: 0;
  color: #6f6f6f;
  display: inline-block;
  font-size: 21px;
  position: absolute;
  right: 3px;
  text-align: center;
  top: 3px;

  &:hover {
    cursor: pointer;
  }

  &:before {
    content: '\e806';
  }
}

.lcu-icon-eye {

  &:before {
    content: '\e81c';
    font-family: 'lcu';
  }
}

.lcu-enroll-required {

  &:before {
    color: $danger;
    content: '\e819';
    display: inline-block;
    font-family: 'lcu';
    font-size: 9px;
    margin-left: 5px;
    position: relative;
    top: -2px;
  }
}

.lcu-form-risen {

  .lcu-enroll-required {

    &:before {
      font-size: 7px;
    }
  }
}

.lcu-enroll-errorMsg {
  color: $danger;
  display: inline-block;
  font-size: 16px;
}

.lcu-enroll-btn {
  background-color: $legacy-primary;
  color: $black;
  padding: 12px 0;

  &:hover {
    background-color: darken($legacy-primary, 5);
  }
}

.lcu-enroll-inputError {
  background-color: #ffe6e6;
  border-color: $danger;

  &:focus-visible {
    border-color: $danger;
  }

  &:focus {
    border-color: $danger;
  }

  &:focus-within {
    border-color: $danger;
  }
}

.lcu-enroll-footer {
  color: rgba($black, .6);
  padding-top: 50px;
  text-align: center;
}

.lcu-powered-link {
  color: $legacy-primary;
  font-size: 13px;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.lcu-login-link {
  color: $legacy-primary;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.lcu-mobile-bump {

  @media only screen and (max-width: $mobile-max) {
    margin-bottom: 35px;
  }
}

.lcu-login-smallP {
  color: rgba($black, .6);
  text-align: center;
}

.lcu-enroll-formError {
  background-color: #ffe8e8;
  border: 2px solid $danger;
  border-radius: 3px;
  padding: 20px;
}

.lcu-back-icon {
    &:before {
    display: inline-block;
    content: '\e81f';
    font-family: "lcu";
    margin-right: 5px;
    font-weight: bold;
  }
}

.lcu-summary-box {
  padding: 25px 20px;
  border-radius: 3px;
  background-color: $off-white;
}

.lcu-summary-item {
  display: grid;
  grid-template-columns: max-content 1fr;
  grid-gap: 25px;
}

.lcu-summary-itemImage {
  width: 140px;
  border: 1px solid $gray;
}

.lcu-summary-totals {
  display: grid;
  grid-template-columns: 1fr max-content;
  grid-column-gap: 30px;
  grid-row-gap: 10px;

  span {
    justify-self: end;
  }
}

.lcu-summary-total {
  font-family: raleway-extra-bold;
  font-size: 18px;
}

.lcu-success-enroll {
  display: inline-block;
  color: $success;
  font-size: 80px;

  &:after {
    font-family: 'lcu';
    content: '\e801';
  }
}

.lcu-success-msg {
  font-size: 24px;
  text-align: center;
  font-family: 'raleway-bold';
}

.lcu-forward-btn {
    &:after {
      display: inline-block;
      font-family: 'lcu';
      content: '\e80a';
      margin-left: 10px;
  }
}

.lcu-form-checkbox {
  background-color: rgba($black, .05);

  &:checked {
    background-color: $legacy-primary;
  }
}

.full-width-btn {
  width: 100%;
}

.lcu-fee-price {
  text-transform: uppercase;
}

.lcu-discount-input {
  display: grid;
  grid-template-columns: 1fr max-content;
  grid-gap: 10px;
  justify-content: end;
  align-items: center;
  margin-bottom: 5px;

  &:last-child {
    margin-bottom: 0;
  }
}

.lcu-register-inputError {
  background-color: #ffe6e6;
  border-color: $danger;

  &:focus-visible {
    border-color: $danger;
  }

  &:focus {
    border-color: $danger;
  }

  &:focus-within {
    border-color: $danger;
  }
}

.lcu-discount-errorMsg {
  color: $danger;
  display: inline-block;
  font-size: 16px;
}

.lcu-summary-savings {
  color: $success;
  font-weight: bold;
}

.lcu-input-discount {
  color: $legacy-primary;
}

.lcu-disabled-input {
  background-color: $gray-lightest;
  color: #818181;

  &:hover {
    cursor: not-allowed;
  }

  &:focus {
    border-color: transparent;
  }

  &:focus-within {
    border-color: transparent;
  }
}

.coming-soon {
  height: 250px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 21px;
}
</style>
